import { todayIso, addDays, transferInPeriod, type AssistantDraft, type Share, type SharedOperation, type Category, type CategoryKind, type Dashboard, type Debt, type PeriodItem, type Transaction } from '@fin/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { authStore, del, qs } from '../../lib/api.ts';
import { categoryLabel, useCategories, useContacts, useDebts, useGoals, useWallets } from '../../lib/data.ts';
import { draftChanges } from '../../lib/draft.ts';
import { kindLabels, money, rub } from '../../lib/format.ts';
import { categoryColor, categoryIcon, kindTint, walletIcon } from '../../lib/icons.tsx';

import { invalidate, invalidateMoney, useQuery } from '../../lib/query.ts';
import { OtherFields, PhraseInput, TargetChips, draftHint, targetActions, targetOf, targetTitles, useOtherEntry, type Target } from '../assistant/public.ts';
import { Button, ConfirmButton, Field, Icon, Input, Modal, NumberInput, Segmented, Select, Tile, cx, toast, useAction, useIsMobile } from '../../components/ui.tsx';
import { type Mode, type TxDefaults, planLineDefaults, modeOf, repeatDefaults } from './model.tsx';
import { isOp, evaluate, hasOp, formatExpr, exprOf, normalizeTyped } from './amount.tsx';
import { CategoryPicker, WalletPicker } from './pickers.tsx';
import { saveTransaction } from './save.ts';
import { ShareReturnPicker, SharesEditor, SharesPanel, inputDrafts, sharesOf, sharesTotal, type ShareDraft } from './shares.tsx';

export const memo = {
  get(key: string): number | undefined {
    try { const v = Number(localStorage.getItem(`fin.last.${key}`)); return v || undefined; } catch { return undefined; }
  },
  set(key: string, v: number | undefined) {
    try { if (v) localStorage.setItem(`fin.last.${key}`, String(v)); } catch { /* storage unavailable */ }
  },
};

export const debtKinds: CategoryKind[] = ['debt', 'repayment', 'loan', 'debt_collection'];

export function TransactionModal({ tx, onClose, defaults, words }: { tx?: Transaction; onClose: () => void; defaults?: TxDefaults; words?: boolean }) {
  const mobile = useIsMobile();
  const wallets = useWallets();
  const cats = useCategories();
  const contacts = useContacts();
  const debts = useDebts();
  const goals = useGoals();
  const dash = useQuery<Dashboard>('/dashboard');
  const { busy, run } = useAction();
  const user = authStore.get().user;

  const allWallets = wallets.data ?? [];
  // an edited transfer opens from whichever leg was tapped: «Откуда», «Куда» and both sums
  const [legs] = useState(() => tx?.transferId ? { ...repeatDefaults(tx), received: tx.amount > 0 ? tx.amount : tx.counterpartAmount != null ? Math.abs(tx.counterpartAmount) : undefined } : null);
  const activeWallets = allWallets.filter((w) => !w.archived || w.id === tx?.walletId || w.id === tx?.counterpartWalletId);
  const [mode, setMode] = useState<Mode>(tx ? modeOf(tx.categoryKind) : defaults?.mode ?? 'expense');
  const [debtKind, setDebtKind] = useState<CategoryKind>(tx && debtKinds.includes(tx.categoryKind) ? tx.categoryKind : defaults?.debtKind ?? 'repayment');
  const [walletId, setWalletId] = useState<number | ''>((legs ? legs.walletId : tx?.walletId) ?? defaults?.walletId ?? '');
  // after a swap with no «Куда» chosen, «Откуда» stays empty instead of falling back to the primary wallet
  const [noFrom, setNoFrom] = useState(false);
  const [toWalletId, setToWalletId] = useState<number | ''>(legs?.toWalletId ?? defaults?.toWalletId ?? '');
  const [categoryId, setCategoryId] = useState<number | ''>(legs ? '' : tx?.categoryId ?? defaults?.categoryId ?? '');
  const [expr, setExpr] = useState(exprOf(legs ? legs.amount : tx?.amount ?? defaults?.amount));
  const [amountTo, setAmountTo] = useState<number | null>(legs?.received ?? defaults?.amountTo ?? null);
  const [fee, setFee] = useState<number | null>(null);
  const [date, setDate] = useState(tx?.date ?? todayIso());
  const [note, setNote] = useState(tx?.note ?? defaults?.note ?? '');
  const [contactId, setContactId] = useState<number | ''>(tx?.contactId ?? defaults?.contactId ?? '');
  const [newContact, setNewContact] = useState('');
  const [debtId, setDebtId] = useState<number | ''>(tx?.debtId ?? defaults?.debtId ?? '');
  const [lineId, setLineId] = useState<number | null>(tx?.periodItemId ?? (tx?.amount && tx.amount > 0 ? tx.counterpartPeriodItemId : null) ?? defaults?.periodItemId ?? null);
  const [exclude, setExclude] = useState(tx?.excludeFromReport ?? defaults?.excludeFromReport ?? false);
  // «Учитывать в лимите периода»; null until touched: then the default follows the type and wallets
  const [periodFlag, setPeriodFlag] = useState<boolean | null>(tx ? tx.inPeriod : null);
  // «Добавить к свободным деньгам периода»: off by default, the salary is already the budget
  const [periodIncome, setPeriodIncome] = useState(tx?.periodIncome ?? false);
  const [shares, setShares] = useState<ShareDraft[]>(() => inputDrafts(defaults?.shares));
  // a share of a shared operation picked as the debt this return closes (shares are not in «Долги»)
  const [returnShare, setReturnShare] = useState<{ share: Share; currency: string } | null>(null);
  const [picker, setPicker] = useState<'category' | 'wallet' | 'toWallet' | null>(null);
  // the keypad is out for a new operation; editing opens with the details visible
  const [pad, setPad] = useState(!tx && !defaults?.amount && !words);
  // one id per new operation: a repeated tap or a retry from the offline queue cannot create it twice
  const [clientId] = useState(() => crypto.randomUUID());

  // a parsed phrase changes only what was said anew (lib/draft.ts)
  const [draft, setDraft] = useState<AssistantDraft | null>(null);
  const heard = useRef<Partial<AssistantDraft>>({});
  // the words sheet adds whatever the request is: an operation, a purchase, an event or a category
  const [target, setTarget] = useState<Target>('operation');
  const other = useOtherEntry();
  const saidIntent = useRef<string | null>(null);
  // the phrase was cleared: the filled fields stay, the next phrase applies every field it names
  const forgetPhrase = () => { heard.current = {}; other.forget(); saidIntent.current = null; setDraft(null); };
  // «Распознать заново»: the form starts over from the phrase; what it does not name goes back to the defaults
  const resetForm = () => {
    setMode('expense'); setDebtKind('repayment'); setWalletId(''); setNoFrom(false); setToWalletId(''); setCategoryId('');
    setExpr(''); setAmountTo(null); setFee(null); setDate(todayIso()); setNote(''); setContactId(''); setNewContact('');
    setDebtId(''); setLineId(null); setExclude(false); setPeriodFlag(null); setPeriodIncome(false); setShares([]); setReturnShare(null);
  };
  const applyDraft = (d: AssistantDraft, fresh?: boolean) => {
    setDraft(d);
    // the sheet follows the request when it changes kind; a kind picked by hand stays while the request keeps its own
    const to = targetOf[d.intent];
    if (to && (fresh || d.intent !== saidIntent.current)) { setTarget(to); if (to !== 'operation') setPad(false); }
    if (to) saidIntent.current = d.intent;
    if (to && to !== 'operation') { other.apply(d, !!fresh); return; }
    if (fresh && d.mode) { resetForm(); heard.current = {}; }
    const { changes: c, heard: next } = draftChanges(heard.current, d);
    heard.current = next;
    // a plan line said by name fills what the phrase leaves out, like a tap on the line
    const line = c.lineId ? dash.data?.currentPeriod?.items.find((i) => i.id === c.lineId) : undefined;
    if (line) {
      setPeriodIncome(false);
      const l = planLineDefaults(line, goals.data ?? []);
      setLineId(line.id);
      if (!c.mode && l.mode) { setMode(l.mode); setCategoryId(''); }
      if (l.debtKind) setDebtKind(l.debtKind);
      if (l.debtId) setDebtId(l.debtId);
      if (l.toWalletId) setToWalletId(l.toWalletId);
      if (l.categoryId) setCategoryId(l.categoryId);
      if (l.amount && !d.amount) setExpr(exprOf(l.amount));
      if (!d.note) setNote(line.name);
    }
    if (c.mode) { setMode(c.mode); setCategoryId(''); }
    if (c.debtKind) { setDebtKind(c.debtKind); setCategoryId(''); setDebtId(''); }
    if (c.amount) setExpr(exprOf(c.amount));
    if (c.walletId === null) { setWalletId(''); setNoFrom(true); } else if (c.walletId) { setWalletId(c.walletId); setNoFrom(false); }
    if (c.toWalletId) setToWalletId(c.toWalletId);
    if (c.categoryId) setCategoryId(c.categoryId);
    if (c.date) setDate(c.date);
    if (c.note) setNote(c.note);
    if (c.fee !== undefined) setFee(c.fee || null);
    if (c.amountTo) setAmountTo(c.amountTo);
    if (c.excludeFromReport !== undefined) setExclude(c.excludeFromReport);
    if (c.contactId) { setContactId(c.contactId); setNewContact(''); }
    if (c.debtId) setDebtId(c.debtId);
  };
  const options = draft?.mode && draft.mode !== 'transfer' && draft.mode === mode && (mode !== 'debt' || draft.debtKind === debtKind)
    ? draft.categoryOptions.map((id) => cats.data?.find((c) => c.id === id)).filter((c): c is Category => !!c) : [];

  // a saved operation changes its type too, except a transfer (both legs) and a debt-linked one (its debt follows the type)
  const retypable = !tx || (!tx.transferId && !tx.debtId);
  const amount = (() => { const v = evaluate(expr); return v !== null && v > 0 ? v : null; })();
  const pending = hasOp(expr);
  // the primary wallet is preselected; before it is known, the most used base-currency bank one
  const fallbackWallet = (() => {
    const primary = dash.data?.primaryWallet?.id ?? user?.primaryWalletId ?? undefined;
    if (primary && activeWallets.some((w) => w.id === primary)) return primary;
    const base = activeWallets.filter((w) => !w.hidden && w.currency === 'RUB' && w.type === 'bank');
    return [...(base.length ? base : activeWallets)].sort((a, b) => b.transactionCount - a.transactionCount)[0]?.id;
  })();
  const wid = walletId || (noFrom ? undefined : fallbackWallet);
  const wallet = activeWallets.find((w) => w.id === wid);
  const toWallet = activeWallets.find((w) => w.id === toWalletId);
  const kind: CategoryKind = mode === 'debt' ? debtKind : mode === 'transfer' ? 'transfer' : mode;
  const kindCats = useMemo(() => (cats.data ?? []).filter((c) => c.kind === kind && (!c.archived || c.id === tx?.categoryId)), [cats.data, kind, tx]);
  const rememberedCat = memo.get(`cat.${kind}`);
  const cid = categoryId && kindCats.some((c) => c.id === categoryId) ? categoryId
    : rememberedCat && kindCats.some((c) => c.id === rememberedCat) ? rememberedCat : '';
  const category = kindCats.find((c) => c.id === cid);
  const debtOptions = (debts.data ?? []).filter((d: Debt) =>
    (!d.closedAt || d.id === tx?.debtId) && (debtKind === 'repayment' ? d.direction === 'borrowed' : debtKind === 'debt_collection' ? d.direction === 'lent' : false));
  // a payment cannot exceed what is still owed (an edited payment is already counted in «paid»)
  const shareDebt = returnShare && returnShare.share.debtId === debtId ? { id: returnShare.share.debtId, currency: returnShare.currency, remaining: returnShare.share.remaining } : undefined;
  const payDebt = mode === 'debt' && (debtKind === 'repayment' || debtKind === 'debt_collection') ? debtOptions.find((d) => d.id === debtId) ?? shareDebt
    : mode === 'income' ? shareDebt : undefined;
  const owed = payDebt && payDebt.currency === wallet?.currency ? payDebt.remaining + (tx?.debtId === payDebt.id ? Math.abs(tx.amount) : 0) : null;
  const overpay = owed !== null && amount !== null && amount > owed + 0.005;

  // lines of the current period still waiting for money: one tap fills the operation from them
  const period = dash.data?.currentPeriod ?? null;
  // an existing operation can be linked too, when it falls into the current period
  const inPeriod = !!period && (!tx || (tx.date >= period.startDate && tx.date <= period.endDate));
  const openLines = inPeriod ? period!.items.filter((i) => i.included && i.section !== 'foreign' && (!i.paid || i.id === lineId)) : [];
  const applyLine = (item: PeriodItem) => {
    if (lineId === item.id) { setLineId(null); return; }
    setPeriodIncome(false);
    // linking an existing operation changes nothing else in it
    if (tx) { setLineId(item.id); return; }
    const d = planLineDefaults(item, goals.data ?? []);
    setLineId(item.id);
    setMode(d.mode ?? 'expense');
    if (d.debtKind) setDebtKind(d.debtKind);
    if (d.debtId) setDebtId(d.debtId);
    if (d.toWalletId) setToWalletId(d.toWalletId);
    if (d.categoryId) setCategoryId(d.categoryId);
    if (d.amount) setExpr(exprOf(d.amount));
    setNote(item.name);
    setShares(inputDrafts(d.shares));
  };

  const base = dash.data?.baseCurrency ?? 'RUB';
  // an outflow can stay out of the period's free money; a new transfer starts from the wallets' rule
  const limitable = kind === 'expense' || kind === 'transfer' || kind === 'repayment' || kind === 'loan';
  const periodDefault = mode === 'transfer' ? !!wallet && !!toWallet && transferInPeriod(wallet, toWallet, base) : true;
  const countsInPeriod = periodFlag ?? periodDefault;
  // a shared plan line brings its own shares (with the returns made) when it is paid
  const lineShared = !!period?.items.find((i) => i.id === lineId)?.shares?.length;
  const shareable = !tx && !lineShared && (mode === 'expense' || mode === 'transfer');
  const othersPart = shareable ? sharesTotal(shares) : 0;

  // how much stays free in the period after this operation: a line with room absorbs it
  const after = (() => {
    if (!period || !amount || tx) return null;
    if (mode === 'transfer' && periodIncome) return toWallet?.currency === base ? period.summary.remainder + (amountTo ?? amount) : null;
    if (wallet?.currency !== base) return null;
    if (mode === 'income') return periodIncome ? period.summary.remainder + amount : null;
    const line = period.items.find((i) => i.id === lineId);
    if (!countsInPeriod && !line) return null;
    const target = line ?? (mode === 'expense' ? period.items.find((i) => i.included && !i.paid && i.section !== 'foreign' && i.categoryId && (i.categoryId === cid || i.categoryId === category?.parentId)) : undefined);
    const room = target && !target.paid ? Math.max(0, target.amount - target.spent) : 0;
    return period.summary.remainder - Math.max(0, amount - othersPart - room);
  })();

  const save = () => run(async () => {
    if (!amount) throw new Error(pending ? 'Выражение не даёт положительную сумму' : 'Укажите сумму');
    if (!wid) throw new Error('Выберите кошелёк');
    if (overpay) throw new Error(`Сумма больше остатка долга: ${money(owed, wallet!.currency)}`);
    if (mode !== 'transfer' && !cid) throw new Error('Выберите категорию');
    if (othersPart > amount + 0.005) throw new Error('Доли других больше суммы операции');
    const { queued, contactChanged } = await saveTransaction({
      id: tx?.id, mode, kind, clientId, walletId: wid, categoryId: cid || 0,
      toWalletId: toWalletId || undefined, amount, amountTo: amountTo ?? undefined, fee: fee ?? undefined,
      date, note, contactId: contactId || null, debtId: debtId || null, periodItemId: lineId, excludeFromReport: exclude,
      inPeriod: limitable ? periodFlag ?? (mode === 'transfer' ? undefined : true) : undefined,
      periodIncome: kind === 'income' || mode === 'transfer' ? periodIncome : undefined, shares: shareable ? sharesOf(shares) : undefined,
      newContact, currency: wallet!.currency, destinationCurrency: toWallet?.currency,
      debtTitle: note || newContact || contacts.data?.find(c => c.id === contactId)?.name || (kind === 'debt' ? 'Долг' : 'Займ'),
      label: mode === 'transfer' ? `перевод ${money(amount, wallet!.currency)}, ${wallet!.name} → ${toWallet?.name ?? ''}` : `${money(amount, wallet!.currency)}, ${category?.name ?? kindLabels[kind]}`,
    });
    if (contactChanged) invalidate('/contacts');
    if (mode !== 'transfer') memo.set(`cat.${kind}`, cid || undefined);
    invalidateMoney();
    onClose();
    toast(queued ? 'Нет связи — операция сохранена на телефоне и отправится сама' : tx ? 'Операция сохранена' : 'Операция добавлена');
  });

  const op = target === 'operation';
  const saveOther = () => run(async () => {
    if (op) return;
    const done = await other.save(target);
    onClose();
    toast(done);
  });

  // «Откуда» and «Куда» trade places; across currencies the two sums trade places with them
  const swapWallets = () => {
    if (!wid && !toWalletId) return;
    const cross = !!toWallet && !!wallet && toWallet.currency !== wallet.currency;
    setWalletId(toWalletId);
    setNoFrom(!toWalletId);
    setToWalletId(wid ?? '');
    if (cross) { setExpr(exprOf(amountTo)); setAmountTo(amount); }
  };

  // a long press on ⌫ clears the whole amount (Money Lover's «C»); the click that follows is skipped
  const hold = useRef<{ timer?: number; fired?: boolean }>({});
  const holdStart = () => {
    hold.current.fired = false;
    hold.current.timer = window.setTimeout(() => { hold.current.fired = true; setExpr(''); navigator.vibrate?.(10); }, 450);
  };
  const holdEnd = () => window.clearTimeout(hold.current.timer);

  const key = (k: string) => {
    if (k === '⌫' && hold.current.fired) { hold.current.fired = false; return; }
    setExpr((e) => {
      const last = e[e.length - 1];
      const segment = e.split(/[+−×÷]/).pop() ?? '';
      if (k === '⌫') return e.slice(0, -1);
      if (k === '=') { const v = evaluate(e); return v !== null && v > 0 ? exprOf(v) : e; }
      if (isOp(k)) {
        if (!e) return e;
        if (isOp(last)) return e.slice(0, -1) + k;
        return e.length >= 40 ? e : (last === ',' ? e.slice(0, -1) : e) + k;
      }
      if (k === ',') return segment.includes(',') ? e : e + (segment ? ',' : '0,');
      if (segment.includes(',') && segment.split(',')[1].length >= 2) return e;
      if (segment.replace(',', '').length >= 10) return e;
      if (k === '000') return segment && segment !== '0' && !segment.includes(',') ? e + '000' : e;
      if (segment === '0') return e.slice(0, -1) + k;
      return e + k;
    });
  };

  const amountTone = mode === 'expense' ? 'neg' : mode === 'income' ? 'pos' : mode === 'debt' ? 'debt' : '';
  const dateLabel = date === todayIso() ? 'Сегодня' : date === addDays(todayIso(), -1) ? 'Вчера' : null;
  const shownAmount = pending ? evaluate(expr) : amount;
  // while an expression is typed the big line shows its value (never below zero: the sum has no sign)
  const bigText = expr ? formatExpr(pending ? (shownAmount !== null && shownAmount > 0 ? exprOf(shownAmount) : '0') : expr) : '0';
  const sizeClass = bigText.length > 12 ? 'sz-s' : bigText.length > 9 ? 'sz-m' : '';
  const remove = tx ? async () => { await del(`/transactions/${tx.id}`); invalidateMoney(); onClose(); } : null;

  // a press anywhere in the sheet outside the keypad and the amount puts the keypad away;
  // switching the type keeps it, since the amount usually comes next
  const onSheetPointer = (e: React.PointerEvent) => {
    if (!mobile || !pad) return;
    const t = e.target as Element;
    if (t.closest('.keypad, .qa-amount, .qa-dock, .qa-head .segmented')) return;
    setPad(false);
  };

  return (
    <Modal sheet title={tx ? 'Операция' : targetTitles[target]} onClose={onClose} className="modal-qa" bodyClass={cx('qa', mobile && pad && 'qa-pad-open')}
      onPointerDownCapture={onSheetPointer}
      footer={mobile ? undefined : !op ? (
        <>
          <span />
          <div className="row-gap">
            <Button onClick={onClose}>Отмена</Button>
            <Button variant="primary" busy={busy} onClick={saveOther}>{targetActions[target as Exclude<Target, 'operation'>]}</Button>
          </div>
        </>
      ) : (
        <>
          {remove ? <ConfirmButton size="md" onConfirm={remove}>Удалить</ConfirmButton> : <span />}
          <div className="row-gap">
            <Button onClick={onClose}>Отмена</Button>
            <Button variant="primary" busy={busy} onClick={save} disabled={!amount || overpay}>{tx ? 'Сохранить' : 'Добавить'}</Button>
          </div>
        </>
      )}
      dock={mobile && op ? (
        <div className="qa-dock">
          <div className={cx('keypad', !pad && 'away')} aria-label="Клавиатура суммы" aria-hidden={!pad} inert={!pad}>
            <button type="button" className="key key-op" onClick={() => key('+')} aria-label="Плюс">+</button>
            <button type="button" className="key key-op" onClick={() => key('−')} aria-label="Минус">−</button>
            <button type="button" className="key key-op key-eq-op" onClick={() => key('=')} aria-label="Посчитать" disabled={!pending}>=</button>
            <button type="button" className="key key-op" onClick={() => setExpr('')} aria-label="Очистить сумму" disabled={!expr}>C</button>
            {['1', '2', '3'].map((k) => <button key={k} type="button" className="key" onClick={() => key(k)}>{k}</button>)}
            <button type="button" className="key key-dim" aria-label="Стереть, долгое нажатие — очистить" onClick={() => key('⌫')}
              onPointerDown={holdStart} onPointerUp={holdEnd} onPointerLeave={holdEnd} onPointerCancel={holdEnd} onContextMenu={(e) => e.preventDefault()}>
              <Icon name="backspace" size={24} />
            </button>
            {['4', '5', '6'].map((k) => <button key={k} type="button" className="key" onClick={() => key(k)}>{k}</button>)}
            <button type="button" className="key key-dim" aria-label="Запятая" onClick={() => key(',')}>,</button>
            {['7', '8', '9'].map((k) => <button key={k} type="button" className="key" onClick={() => key(k)}>{k}</button>)}
            <button type="button" className="key key-ok" aria-label={tx ? 'Сохранить' : 'Добавить'}
              disabled={!amount || busy || overpay} onClick={() => void save()}>
              <Icon name="check" size={30} />
            </button>
            <button type="button" className="key key-dim" onClick={() => key('000')}>000</button>
            <button type="button" className="key" onClick={() => key('0')}>0</button>
            <button type="button" className="key key-dim" aria-label="Скрыть клавиатуру" onClick={() => setPad(false)}><Icon name="down" size={22} /></button>
          </div>
        </div>
      ) : undefined}>
      {/* nothing is pinned above the fields: the type and the amount scroll with them */}
      <div className="qa-head">
        {words ? <PhraseInput onDraft={applyDraft} onClear={forgetPhrase} hint={draftHint(draft)} /> : null}
        {words && !tx ? <TargetChips value={target} onChange={(t) => { setTarget(t); if (t !== 'operation') setPad(false); }} /> : null}
        {op && options.length > 1 ? (
          <div className="qa-lines">
            <span className="qa-lines-label">Категория</span>
            <div className="chips">
              {options.map((c) => (
                <button key={c.id} type="button" className={cx('chip-btn', cid === c.id && 'on')} aria-pressed={cid === c.id} onClick={() => setCategoryId(c.id)}>
                  {categoryLabel(c, cats.data ?? [])}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {op && retypable ? (
          <Segmented wide label="Тип операции" value={mode} onChange={(m) => { setMode(m); setCategoryId(''); if (returnShare) { setReturnShare(null); setDebtId(''); setExclude(false); } }} options={[
            { value: 'expense', label: 'Расход' }, { value: 'income', label: 'Доход' },
            ...(tx ? [] : [{ value: 'transfer' as Mode, label: 'Перевод' }]), { value: 'debt', label: 'Долг' },
          ]} />
        ) : null}
        {!op ? null : mobile ? (
          <button type="button" className={cx('qa-amount', pad && 'on')} onClick={() => setPad(true)} aria-label={`Сумма ${shownAmount ? money(shownAmount, wallet?.currency ?? 'RUB') : 'не указана'}${pad ? '' : ', показать клавиатуру'}`} aria-expanded={pad}>
            <span className="qa-expr" aria-hidden>{pending ? formatExpr(expr) : ' '}</span>
            <span className="qa-amount-line">
              <span className={cx('qa-amount-value', amountTone, sizeClass, !expr && 'placeholder')}>{bigText}</span>
              <span className="qa-amount-cur">{wallet?.currency ?? 'RUB'}</span>
            </span>
          </button>
        ) : (
          <div className="qa-amount-desk">
            <input className={cx('input qa-amount-input', amountTone)} inputMode="decimal" value={formatExpr(expr).replace(/[  ]/g, (c) => (c === ' ' ? ' ' : ''))} autoFocus={!words} data-autofocus={!words || undefined} placeholder="0" aria-label="Сумма, можно с + − × ÷"
              onChange={(e) => setExpr(normalizeTyped(e.target.value))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (pending) key('='); else void save(); } }} />
            <span className="qa-expr-desk">{pending && shownAmount !== null ? `= ${money(shownAmount, wallet?.currency ?? 'RUB')}` : ' '}</span>
          </div>
        )}
        {op ? <p className="qa-hint" aria-live="polite">
          {overpay ? <span className="neg">Больше остатка долга: <span className="money">{money(owed, wallet?.currency ?? 'RUB')}</span></span>
            : after !== null ? <>Свободно в периоде останется <span className={cx('money', after < 0 ? 'neg' : 'pos')}>{rub(after)}</span></> : ' '}
        </p> : null}
      </div>
      {!op ? <OtherFields target={target as Exclude<Target, 'operation'>} entry={other.entry} set={other.set} /> : null}
      {op && !tx && (mode === 'income' || (mode === 'debt' && debtKind === 'debt_collection')) ? (
        <ShareReturnPicker debtId={debtId} onPick={(o: SharedOperation, s: Share | null) => {
          if (!s) { setReturnShare(null); setDebtId(''); return; }
          // a return is ordinary income «Прочие поступления», kept out of reports (the spending was the owner's part only)
          const other = cats.data?.find((c) => c.kind === 'income' && !c.archived && c.name.trim().toLowerCase() === 'прочие поступления');
          setReturnShare({ share: s, currency: o.currency });
          setMode('income'); setCategoryId(other?.id ?? ''); setExclude(true);
          setDebtId(s.debtId); setContactId(s.contactId ?? ''); setNewContact('');
          setExpr(exprOf(s.remaining)); setNote(`${s.contactName ?? 'Возврат'}: ${o.note || o.categoryName}`);
        }} />
      ) : null}
      {op && mode === 'debt' && !tx ? (
        <Select aria-label="Вид долговой операции" value={debtKind} onChange={(e) => { setDebtKind(e.target.value as CategoryKind); setCategoryId(''); }}>
          {debtKinds.map((k) => <option key={k} value={k}>{kindLabels[k]}</option>)}
        </Select>
      ) : null}

      {op ? <>
      {openLines.length ? (
        <div className="qa-lines">
          <span className="qa-lines-label">{tx ? 'Оплачивает строку плана' : 'Из плана периода'}</span>
          <div className="chips">
            {openLines.map((i) => (
              <button key={i.id} type="button" className={cx('chip-btn', lineId === i.id && 'on')} aria-pressed={lineId === i.id} onClick={() => applyLine(i)}>
                <span>{i.name}</span><span className="money muted">{rub(Math.max(0, i.amount - i.spent) || i.amount)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="qa-rows">
        {mode !== 'transfer' ? (
          <button type="button" className="qa-row" onClick={() => { setPad(false); setPicker('category'); }}>
            {category ? <Tile icon={categoryIcon(category)} color={categoryColor(category)} image={category.iconImage} /> : <Tile icon="folder" color={kindTint[kind]} />}
            <span className="qa-row-text">
              <span className="qa-row-label">Категория</span>
              <span className={cx('qa-row-value', !category && 'placeholder')}>{category ? categoryLabel(category, cats.data ?? []) : 'Выбрать'}</span>
            </span>
            <span className="qa-row-aside"><Icon name="right" size={18} /></span>
          </button>
        ) : null}
        <button type="button" className="qa-row" onClick={() => { setPad(false); setPicker('wallet'); }}>
          {wallet ? <Tile icon={walletIcon(wallet)} color={wallet.color} /> : <Tile icon="wallet" color="#8F8A7E" />}
          <span className="qa-row-text">
            <span className="qa-row-label">{mode === 'transfer' ? 'Откуда' : 'Кошелёк'}</span>
            <span className={cx('qa-row-value', !wallet && 'placeholder')}>{wallet?.name ?? 'Выбрать'}</span>
          </span>
          <span className="qa-row-aside">{wallet ? <span className="money">{money(wallet.balance, wallet.currency)}</span> : null}<Icon name="right" size={18} /></span>
        </button>
        {mode === 'transfer' ? (
          <button type="button" className="qa-swap" aria-label="Поменять счета местами" disabled={!wid && !toWalletId} onClick={swapWallets}>
            <Icon name="swap-v" size={18} />
          </button>
        ) : null}
        {mode === 'transfer' ? (
          <button type="button" className="qa-row" onClick={() => { setPad(false); setPicker('toWallet'); }}>
            {toWallet ? <Tile icon={walletIcon(toWallet)} color={toWallet.color} /> : <Tile icon="wallet" color="#8F8A7E" />}
            <span className="qa-row-text">
              <span className="qa-row-label">Куда</span>
              <span className={cx('qa-row-value', !toWallet && 'placeholder')}>{toWallet?.name ?? 'Выбрать'}</span>
            </span>
            <span className="qa-row-aside">{toWallet ? <span className="money">{money(toWallet.balance, toWallet.currency)}</span> : null}<Icon name="right" size={18} /></span>
          </button>
        ) : null}
      </div>

      {mode === 'transfer' && toWallet && wallet && toWallet.currency !== wallet.currency ? (
        <Field label={`Получено, ${toWallet.currency}`} hint={amount && amountTo ? `Курс: 1 ${toWallet.currency} = ${(amount / amountTo).toFixed(4)} ${wallet.currency}` : undefined}>
          <NumberInput value={amountTo} onChange={setAmountTo} />
        </Field>
      ) : null}
      {mode === 'transfer' && !tx ? (
        <Field label="Комиссия" hint="Спишется отдельным расходом из кошелька-отправителя"><NumberInput value={fee} onChange={setFee} placeholder="0" /></Field>
      ) : null}

      {mode === 'debt' ? (
        <div className="form-row">
          <Field label={debtKind === 'debt' ? 'У кого' : debtKind === 'debt_collection' ? 'От кого' : 'Кому'} className="grow">
            <Select value={contactId} onChange={(e) => setContactId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">—</option>
              {(contacts.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          {!contactId ? <Field label="Или новый контакт" className="grow"><Input value={newContact} onChange={(e) => setNewContact(e.target.value)} placeholder="Имя" /></Field> : null}
        </div>
      ) : null}
      {mode === 'debt' && (debtKind === 'repayment' || debtKind === 'debt_collection') ? (
        <Field label="Какой долг гасится">
          <Select value={debtId} onChange={(e) => setDebtId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Не привязывать</option>
            {debtOptions.map((d) => <option key={d.id} value={d.id}>{d.title} — осталось {money(d.remaining, d.currency)}</option>)}
            {returnShare ? <option value={returnShare.share.debtId}>Доля: {returnShare.share.contactName ?? '—'} — осталось {money(returnShare.share.remaining, returnShare.currency)}</option> : null}
          </Select>
        </Field>
      ) : null}

      <div className="qa-date">
        <button type="button" className={cx('chip-btn', dateLabel === 'Сегодня' && 'on')} onClick={() => setDate(todayIso())}>Сегодня</button>
        <button type="button" className={cx('chip-btn', dateLabel === 'Вчера' && 'on')} onClick={() => setDate(addDays(todayIso(), -1))}>Вчера</button>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Дата" />
      </div>
      <NoteField value={note} onChange={setNote} categoryId={mode === 'transfer' ? null : cid || null} />
      {shareable ? (shares.length ? (
        <div className="shares-panel">
          <span className="qa-lines-label">Доли других</span>
          <SharesEditor value={shares} onChange={setShares} contacts={contacts.data ?? []} total={amount} currency={wallet?.currency ?? base} />
        </div>
      ) : (
        <Button size="sm" className="share-add" onClick={() => { setPad(false); setShares([{ key: crypto.randomUUID(), contactId: '', contactName: '', amount: null }]); }}>
          <Icon name="plus" size={16} /> Разделить с другими
        </Button>
      )) : null}
      {tx && (kind === 'expense' || kind === 'transfer') ? (
        <SharesPanel transactionId={tx.id} total={legs?.amount ?? Math.abs(tx.amount)} currency={tx.transferId && tx.amount > 0 ? tx.counterpartCurrency ?? tx.currency : tx.currency} contacts={contacts.data ?? []} />
      ) : null}
      {limitable ? (
        <label className="check"><input type="checkbox" checked={countsInPeriod && !periodIncome} onChange={(e) => { setPeriodFlag(e.target.checked); if (e.target.checked) setPeriodIncome(false); }} /> Учитывать в лимите периода</label>
      ) : null}
      {kind === 'income' || mode === 'transfer' ? (
        <label className="check"><input type="checkbox" checked={periodIncome} disabled={mode === 'transfer' && !!lineId} onChange={(e) => { setPeriodIncome(e.target.checked); if (mode === 'transfer' && e.target.checked) setPeriodFlag(false); }} /> Добавить к свободным деньгам периода</label>
      ) : null}
      {mode !== 'transfer' ? (
        <label className="check"><input type="checkbox" checked={exclude} onChange={(e) => setExclude(e.target.checked)} /> Не учитывать в отчётах</label>
      ) : null}
      </> : null}

      {mobile && !op ? (
        <div className="qa-actions">
          <Button variant="primary" className="btn-lg grow" busy={busy} onClick={saveOther}>{targetActions[target as Exclude<Target, 'operation'>]}</Button>
        </div>
      ) : mobile ? (
        <div className="qa-actions">
          {remove ? <ConfirmButton size="md" onConfirm={remove}>Удалить</ConfirmButton> : null}
          <Button variant="primary" className="btn-lg grow" busy={busy} disabled={!amount || overpay} onClick={save}>
            {tx ? 'Сохранить' : amount ? `Добавить ${money(amount, wallet?.currency ?? 'RUB')}` : 'Добавить'}
          </Button>
        </div>
      ) : null}

      {picker === 'category' ? (
        <CategoryPicker kind={kind} value={cid || null} onClose={() => setPicker(null)}
          onTab={!retypable ? undefined : (t) => { setMode(t); setCategoryId(''); }}
          onPick={(id) => {
            // a debt category picked from the «Долг/Займ» tab also says which debt operation this is
            const picked = cats.data?.find((c) => c.id === id);
            if (picked && debtKinds.includes(picked.kind)) setDebtKind(picked.kind);
            setCategoryId(id);
            setPicker(null);
          }} />
      ) : null}
      {picker === 'wallet' || picker === 'toWallet' ? (
        <WalletPicker title={picker === 'toWallet' ? 'Куда перевести' : mode === 'transfer' ? 'Откуда перевести' : 'Кошелёк'}
          value={(picker === 'toWallet' ? toWalletId : wid) || null}
          exclude={picker === 'toWallet' ? wid || undefined : undefined}
          onClose={() => setPicker(null)}
          onPick={(id) => { if (picker === 'toWallet') setToWalletId(id); else { setWalletId(id); setNoFrom(false); } setPicker(null); }} />
      ) : null}
    </Modal>
  );
}

export function NoteField({ value, onChange, categoryId }: { value: string; onChange: (v: string) => void; categoryId: number | null }) {
  const [q, setQ] = useState(value.trim());
  useEffect(() => { const t = window.setTimeout(() => setQ(value.trim()), 250); return () => window.clearTimeout(t); }, [value]);
  const typed = q.length >= 2 ? q : '';
  // with nothing typed the suggestions belong to a category; a typed fragment searches every note
  const key = categoryId || typed ? `/notes${qs({ categoryId: categoryId ?? undefined, q: typed || undefined })}` : null;
  const notes = useQuery<string[]>(key);
  const list = (notes.data ?? []).filter((n) => n !== value.trim());
  return (
    <div className="note-field">
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Примечание" aria-label="Примечание" />
      {list.length ? (
        <div className="chips note-chips" aria-label="Прежние примечания">
          {list.map((n) => <button key={n} type="button" className="chip-btn" onClick={() => onChange(n)}>{n}</button>)}
        </div>
      ) : null}
    </div>
  );
}
